import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useCheckout } from './checkout/useCheckout';
import { DeliveryLocationCard } from './checkout/DeliveryLocationCard';
import { DeliveryDateTimeCard } from './checkout/DeliveryDateTimeCard';
import { AdditionalInfoCard } from './checkout/AdditionalInfoCard';
import { OrderSummaryCard } from './checkout/OrderSummaryCard';
import { NoDeliveryDaysNotice } from './checkout/NoDeliveryDaysNotice';
import { OrderHistoryComparison } from '../../components/insights/OrderHistoryComparison';
import { OverCapPanel } from '../../components/insights/OverCapPanel';
import { getClientPermissions } from '../../lib/clientPermissions';

const Checkout = () => {
  const c = useCheckout();
  const canPlaceOrders = getClientPermissions().can_place_orders;

  if (c.loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="checkout-loading">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (c.noDeliveryDaysConfigured) {
    return <NoDeliveryDaysNotice navigate={c.navigate} />;
  }

  return (
    <div className="space-y-6" data-testid="checkout-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => c.navigate('/client/shop')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Checkout</h1>
          <p className="text-sm text-gray-600 mt-1">Review and submit your order</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Order Details */}
        <div className="lg:col-span-2 space-y-6">
          <DeliveryLocationCard
            profile={c.profile}
            selectedLocation={c.selectedLocation}
            setSelectedLocation={c.setSelectedLocation}
            selectedLocationData={c.selectedLocationData}
          />
          <DeliveryDateTimeCard
            deliveryDate={c.deliveryDate}
            setDeliveryDate={c.setDeliveryDate}
            deliveryTime={c.deliveryTime}
            setDeliveryTime={c.setDeliveryTime}
            isDateDisabled={c.isDateDisabled}
          />
          <AdditionalInfoCard
            poNumber={c.poNumber}
            setPoNumber={c.setPoNumber}
            orderNotes={c.orderNotes}
            setOrderNotes={c.setOrderNotes}
          />
        </div>

        {/* Right Column - Order Summary */}
        <div className="space-y-6">
          <OverCapPanel
            lines={c.overCapLines}
            gated={c.capGatingActive}
            reasons={c.capReasons}
            reasonCode={c.capReason}
            onReasonChange={c.setCapReason}
            note={c.capNote}
            onNoteChange={c.setCapNote}
            onSetQty={c.setItemQuantity}
            reasonMissing={c.capReasonMissing}
          />
          <OrderHistoryComparison cart={c.cart} deliveryDate={c.deliveryDate} />
          <OrderSummaryCard
            cart={c.cart}
            deliveryDate={c.deliveryDate}
            isProductAvailableForDate={c.isProductAvailableForDate}
            getDayName={c.getDayName}
            formatAvailableDays={c.formatAvailableDays}
            getCartSavings={c.getCartSavings}
            cartTotal={c.cartTotal}
            minimumOrder={c.minimumOrder}
            unavailableItems={c.unavailableItems}
            hasUnavailableItems={c.hasUnavailableItems}
            isValidOrder={c.isValidOrder}
            selectedLocation={c.selectedLocation}
            submitting={c.submitting}
            onRemoveItem={c.handleRemoveItem}
            onSubmit={c.handleSubmitOrder}
            canPlaceOrders={canPlaceOrders}
          />
        </div>
      </div>
    </div>
  );
};

export default Checkout;
